import { defineStore } from 'pinia'

import { computed, ref, watch } from 'vue'

import type {
  MainDrawerState,
  DrawerState,
  PersistableRightDrawerContent,
} from '../types/drawer'
import {
  LeftDrawerContent,
  PERSISTABLE_RIGHT_DRAWER_CONTENTS,
  RightDrawerContent,
} from '../types/drawer'

import {
  persistDrawerState,
  persistLeftDrawerCollapsed,
  persistRightDrawerContent,
} from '../api/drawer'
import {
  hasLeftDrawer,
  hasRightDrawer,
  isUsingNewLayout,
} from '@app/adapters/silverfin'

const defaultInitialState: DrawerState = {
  content: '',
  direction: 'initial',
}

function isPersistableRightContent(
  value: RightDrawerContent | null | undefined
): value is PersistableRightDrawerContent {
  return PERSISTABLE_RIGHT_DRAWER_CONTENTS.includes(value ?? null)
}

export const useDrawer = defineStore('drawer', () => {
  const leftCollapsed = ref<boolean>(false)
  const leftContent = ref<LeftDrawerContent | null>(LeftDrawerContent.Default)
  const rightContent = ref<RightDrawerContent | null>(null)

  const open = ref<DrawerState>(defaultInitialState)
  const lastPersistedOpenDrawerState = ref<DrawerState | null>(null)
  const lastClosedOnOpenDrawerState = ref<DrawerState | null>(null)
  const leftHydrating = ref<boolean>(false)
  const rightHydrating = ref<boolean>(false)
  // Only the old layout's `open` watcher reads this. It tracks one drawer at a
  // time, so either flag has to suppress its persist. The new layout watches
  // each side separately, and each of those reads its own flag: a left drawer
  // change must never be suppressed by a right drawer flag, because the two are
  // hydrated by separate callers and either can still be mid-hydration while
  // the other changes.
  const shouldPersistState = computed(
    () => !leftHydrating.value && !rightHydrating.value
  )

  // Every SfDrawerToggle mount requests hydration, and toggles inside
  // lazily-loaded content (e.g. a workflow-review item's message-count toggle)
  // mount long after page load. The preferences they hydrate from are frozen at
  // page-load state (window.Silverfin.currentUserPreferences reads a
  // server-rendered hidden input that persist calls never update), so a late
  // re-hydration would revert any drawer change the user made in between —
  // closing a drawer under them, or swapping a transient file preview back to
  // the persisted content. Only the first hydration takes effect. The flag needs
  // no explicit reset: this store is a page singleton (see makePageSingleton),
  // torn down before each page render, so a new page renders a fresh
  // preferences snapshot and a fresh store with the flag cleared.
  let hydratedOnThisPage = false

  function hydrateFromPreferences({
    collapsed,
    rightContent: right,
  }: {
    collapsed: boolean
    rightContent: PersistableRightDrawerContent
  }): void {
    if (!isUsingNewLayout()) return
    if (hydratedOnThisPage) return

    hydratedOnThisPage = true

    const expectedLeftContent = collapsed ? null : LeftDrawerContent.Default
    const alreadyHydrated =
      leftCollapsed.value === collapsed &&
      leftContent.value === expectedLeftContent &&
      rightContent.value === right
    if (alreadyHydrated) return

    leftCollapsed.value = collapsed
    openDrawerDefault({
      direction: 'left',
      content: collapsed ? null : LeftDrawerContent.Default,
    })
    openDrawerDefault({
      direction: 'right',
      content: right,
    })
  }

  const isLeftDrawerOpen = computed(() =>
    isUsingNewLayout() ? !!leftContent.value : open.value.direction === 'left'
  )
  const isRightDrawerOpen = computed(() =>
    isUsingNewLayout() ? !!rightContent.value : open.value.direction === 'right'
  )

  const isAnyDrawerOpen = computed(
    () => isLeftDrawerOpen.value || isRightDrawerOpen.value
  )

  const rightDrawerContent = computed(() => {
    if (isUsingNewLayout()) return rightContent.value
    if (open.value.direction === 'right') return open.value.content
    return null
  })

  const isOpenDirty = computed(
    () => open.value && open.value.direction !== 'initial'
  )

  function openDrawer(drawer: DrawerState): void {
    if (
      (drawer?.direction === 'left' && !hasLeftDrawer()) ||
      (drawer?.direction === 'right' && !hasRightDrawer())
    ) {
      return
    }

    // we're about to open a drawer.
    // If we already have an open one, store it to reopen it when this new one will eventually close
    if (
      !isUsingNewLayout() &&
      open.value.direction !== 'none' &&
      open.value.content !== '' &&
      open.value.content !== drawer?.content &&
      shouldPersistCurrentState()
    ) {
      lastClosedOnOpenDrawerState.value = open.value
    }

    if (isUsingNewLayout() && drawer?.direction === 'right') {
      const isOpeningTransient =
        drawer.content === RightDrawerContent.FileAttachments ||
        drawer.content === RightDrawerContent.FilePreview
      if (
        isOpeningTransient &&
        rightContent.value !== null &&
        isPersistableRightContent(rightContent.value)
      ) {
        lastClosedOnOpenDrawerState.value = {
          direction: 'right',
          content: rightContent.value,
        }
      } else if (!isOpeningTransient) {
        lastClosedOnOpenDrawerState.value = null
      }
    }

    if (isUsingNewLayout()) {
      if (!drawer) {
        leftContent.value = LeftDrawerContent.Default
        rightContent.value = null
        return
      }

      if (drawer?.direction === 'left') {
        leftContent.value = drawer.content
      } else if (drawer?.direction === 'right') {
        rightContent.value = drawer.content
      }
    } else {
      open.value = drawer || defaultInitialState
    }
  }

  function openLeftDrawerDefault(): void {
    openDrawerDefault({
      content: LeftDrawerContent.Default,
      direction: 'left',
    })
  }

  type OpenDrawerDefaultInput =
    | MainDrawerState
    | { direction: 'left'; content: null }
    | { direction: 'right'; content: null }

  function openDrawerDefault(drawer: OpenDrawerDefaultInput): void {
    if (isUsingNewLayout()) {
      if (drawer.direction === 'left' && leftContent.value === drawer.content) {
        return
      }
      if (
        drawer.direction === 'right' &&
        rightContent.value === drawer.content
      ) {
        return
      }
    }

    if (!isUsingNewLayout() && isOpenDirty.value) {
      return
    }

    if (drawer.direction === 'left') {
      leftHydrating.value = true
    } else {
      rightHydrating.value = true
    }

    if (drawer.content !== null) {
      openDrawer(drawer)
      lastPersistedOpenDrawerState.value = drawer
    } else if (isUsingNewLayout()) {
      if (drawer.direction === 'left') {
        leftContent.value = null
      } else {
        rightContent.value = null
      }
    }
  }

  function openLeftDrawer(): void {
    openDrawer({
      content: LeftDrawerContent.Default,
      direction: 'left',
    })
  }

  function openRightDrawer(
    content: RightDrawerContent,
    skipPersistState: boolean = false
  ): void {
    // No-op when this content is already showing. Callers often pass
    // skipPersistState=true for transient opens; re-entering with the same
    // content would set rightHydrating without a subsequent content change to
    // clear it, which then swallows the next genuine persist. Mirrors
    // openDrawerDefault's same-content early return.
    if (rightDrawerContent.value === content) return

    rightHydrating.value = skipPersistState
    openDrawer({
      content,
      direction: 'right',
    })
  }

  function toggleDrawer(drawer: MainDrawerState): void {
    if (isUsingNewLayout()) {
      const shouldCloseLeft =
        leftContent.value === drawer.content && drawer.direction === 'left'
      const shouldCloseRight =
        rightContent.value === drawer.content && drawer.direction === 'right'

      if (shouldCloseLeft) {
        closeLeftDrawer()
      } else if (shouldCloseRight) {
        closeRightDrawer()
      } else {
        openDrawer(drawer)
      }
    } else {
      if (
        open.value.direction === drawer.direction &&
        open.value.content === drawer.content
      ) {
        closeDrawer(drawer.direction)
      } else {
        openDrawer(drawer)
      }
    }
  }

  function closeRightDrawer(): void {
    closeDrawer('right')
  }

  function closeLeftDrawer(): void {
    closeDrawer('left')
  }

  function closeDrawer(direction: 'left' | 'right'): void {
    // If we have a drawer that was opened when this one was opened, reopen it.
    // In the new layout, only restore if the saved state is for the same direction —
    // otherwise collapsing the left drawer would incorrectly restore a right-drawer state.
    const savedState = lastClosedOnOpenDrawerState.value
    const shouldRestore =
      !!savedState &&
      (!isUsingNewLayout() || savedState.direction === direction)

    if (shouldRestore && savedState) {
      openDrawer(savedState)
      lastClosedOnOpenDrawerState.value = null
    } else {
      if (isUsingNewLayout()) {
        if (direction === 'left') {
          leftContent.value = null
        } else if (direction === 'right') {
          rightContent.value = null
        }
      } else {
        open.value = {
          content: 'default',
          direction: 'none',
        }
      }
    }
  }

  function closeRightDrawerWithoutPersisting(
    reopenLastOpenedPersistedDrawer: boolean
  ): void {
    // If the drawer is about to close try to reopen the last opened persisted drawer state
    if (
      !isUsingNewLayout() &&
      reopenLastOpenedPersistedDrawer &&
      lastPersistedOpenDrawerState.value
    ) {
      openDrawer(lastPersistedOpenDrawerState.value)
      return
    }

    // Nothing to close, and nothing to restore in its place. Only a rightContent
    // change clears rightHydrating, so setting it here would leave it true for
    // the life of the page and swallow the next genuine persist. Mirrors
    // openRightDrawer's same-content guard. The old layout holds its state in
    // `open` instead, where this check does not apply.
    if (
      isUsingNewLayout() &&
      rightContent.value === null &&
      !lastClosedOnOpenDrawerState.value
    ) {
      return
    }

    rightHydrating.value = true
    closeRightDrawer()
  }

  // Closing the left drawer to fit a narrow viewport must not overwrite
  // left_drawer_collapsed. The user widens the window, reloads, and expects the
  // drawer they chose. Carries the same no-op guard as the right variant: only
  // a leftContent change clears leftHydrating.
  function closeLeftDrawerWithoutPersisting(): void {
    if (isUsingNewLayout() && leftContent.value === null) return

    leftHydrating.value = true
    closeLeftDrawer()
  }

  function shouldPersistCurrentState(): boolean {
    return (
      open.value.content !== RightDrawerContent.FileAttachments &&
      open.value.content !== RightDrawerContent.FilePreview
    )
  }

  watch(open, (newValue, oldValue) => {
    if (
      (!isUsingNewLayout() && newValue.direction !== oldValue.direction) ||
      newValue.content !== oldValue.content
    ) {
      if (
        newValue.direction !== 'initial' &&
        newValue.content !== RightDrawerContent.FileAttachments &&
        newValue.content !== RightDrawerContent.FilePreview &&
        newValue.content !== RightDrawerContent.AiChat
      ) {
        if (shouldPersistState.value) {
          void persistDrawerState(newValue)
          lastPersistedOpenDrawerState.value =
            newValue.direction !== 'none' ? newValue : null
        } else {
          leftHydrating.value = false
          rightHydrating.value = false
        }
      }

      if (newValue.direction === 'none') {
        window.eventHub.$emit('drawer:close')
      }

      if (newValue.direction === 'left' || newValue.direction === 'right') {
        window.eventHub.$emit('drawer:open', newValue)
      }
    }
  })

  watch(rightContent, (newValue, oldValue) => {
    if (isUsingNewLayout() && newValue !== oldValue) {
      if (
        newValue !== RightDrawerContent.FileAttachments &&
        newValue !== RightDrawerContent.FilePreview
      ) {
        if (!rightHydrating.value) {
          const isPersistableTransition =
            isPersistableRightContent(newValue) &&
            isPersistableRightContent(oldValue)
          if (isPersistableTransition) {
            void persistRightDrawerContent(newValue ?? null)
          }

          const stateToPersist: DrawerState = newValue
            ? { direction: 'right', content: newValue }
            : { direction: 'none', content: 'default' }
          lastPersistedOpenDrawerState.value = newValue ? stateToPersist : null
        } else {
          rightHydrating.value = false
        }
      }

      if (newValue === null) {
        window.eventHub.$emit('drawer:close', 'right')
      } else {
        window.eventHub.$emit('drawer:open', {
          direction: 'right',
          content: newValue,
        })
      }
    }
  })

  watch(leftContent, (newValue, oldValue) => {
    if (isUsingNewLayout() && newValue !== oldValue) {
      if (!leftHydrating.value) {
        const collapsed = newValue === null
        if (leftCollapsed.value !== collapsed) {
          leftCollapsed.value = collapsed
          void persistLeftDrawerCollapsed(collapsed)
        }

        const stateToPersist: DrawerState | null = newValue
          ? { direction: 'left', content: newValue }
          : null
        lastPersistedOpenDrawerState.value = stateToPersist
      } else {
        leftHydrating.value = false
      }

      if (newValue === null) {
        window.eventHub.$emit('drawer:close', 'left')
      } else {
        window.eventHub.$emit('drawer:open', {
          direction: 'left',
          content: newValue,
        })
      }
    }
  })

  return {
    isAnyDrawerOpen,
    isLeftDrawerOpen,
    isRightDrawerOpen,
    rightDrawerContent,
    leftCollapsed,
    leftContent,
    rightContent,
    open,
    hydrateFromPreferences,
    openDrawer,
    openDrawerDefault,
    openLeftDrawer,
    openLeftDrawerDefault,
    openRightDrawer,
    toggleDrawer,
    closeRightDrawer,
    closeLeftDrawer,
    closeRightDrawerWithoutPersisting,
    closeLeftDrawerWithoutPersisting,
  }
})
